import { useInfiniteQuery, useMutation } from '@tanstack/react-query';
import { useMemo } from 'react';

import BackendService from '../services/backendService';

export const generatorCacheKey = 'generators';
const usePresentationVersion = ({ presentationId }: { presentationId: string }) => {
  const {
    data,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
  } = useInfiniteQuery(
    [presentationId, 'presentationVersion'],
    async ({ pageParam = undefined }: { pageParam?: string }) => {
      return await BackendService.instance.versionControl.listPresentationVersions({
        presentationId,
        cursor: pageParam,
        limit: 10,
      });
    },
    {
      getNextPageParam: lastPage => {
        return lastPage?.[lastPage.length - 1]?.id;
      },

      enabled: Boolean(presentationId),
      staleTime: 1000 * 60 * 60,
    },
  );

  const { mutate: restorePresentationVersion, isLoading: isRestoring } = useMutation(
    async ({
      presentationId,
      versionId,
    }: {
      presentationId: string;
      versionId: string;
      onSuccessFunction?: () => void;
    }) => {
      return await BackendService.instance.versionControl.restorePresentationVersion({
        presentationId,
        versionId,
      });
    },
    {
      onSuccess: (data, { onSuccessFunction }) => {
        if (onSuccessFunction && data) {
          onSuccessFunction();
        }
        return true;
      },
      cacheTime: 1000 * 60 * 5,
    },
  );
  const presentationVersions = useMemo(() => {
    if (!data) return [];
    if (!data.pages) return [];
    return data.pages.flatMap(e => e);
  }, [data]);
  return {
    presentationVersions,
    isFetching,
    isFetchingMore: isFetchingNextPage,
    canFetchMore: hasNextPage,
    fetchNextPage,
    restorePresentationVersion,
    isRestoring,
  };
};

export default usePresentationVersion;
